/**
 * Report favorites share one table with the desktop app, so they must share
 * its key.
 *
 * `tblPM_User_ReportFavorite` (server DB, per user) is read and written by
 * both clients. WinForms stores the PKey of its reports TREE node in
 * `FKey_Report` (frmDietech_Resident_DataGrid_V2.vb `AddToFavorites_Handler`
 * :6064, read back at :756-761 and opened with `FindNodeByKeyID`), not a
 * `tblRS_Report` key. The website used to store `pKey_RS_Report`, so a
 * favorite saved in one client opened a different report (or nothing) in the
 * other: web "Diet Order" (RS 57) showed in the desktop as tree node 57,
 * "General Menu".
 *
 * Now the website writes the tree key for every card the desktop tree has
 * (`SLUG_TO_WINFORMS_TREE_KEY`, built from `Fill_ReportTRL` :580-752 and the
 * report-page Select Case :1900-3200). Cards the desktop has no node for are
 * stored as `WEB_ONLY_FAVORITE_KEY_BASE + pKey_RS_Report`, a range no tree
 * node uses, so they can never open the wrong desktop report.
 *
 * Favorites the website saved before this change carry an RS key and the card
 * name it showed (WinForms leaves `Name` empty unless the user renames the
 * node). `legacyWebFavoriteSlug` recognises them by that name; the catalog
 * page rewrites them to the shared key (`AllReportsNext`).
 */

import { REPORT_CONFIGS } from './reportConfigs';
import { STATIC_REPORTS } from './staticReports';
import { RETIRED_REPORT_REPLACEMENT, currentReportPKey } from './retiredReports';

/** Keys at or above this are website-only cards: `base + pKey_RS_Report`. */
export const WEB_ONLY_FAVORITE_KEY_BASE = 100000;

/**
 * Website card slug -> WinForms reports-tree PKey (the key written).
 * Where one desktop node prints several web cards (Beverage prints all five
 * beverage reports) they share the key, so they star together - the desktop
 * has one favorite for them.
 */
export const SLUG_TO_WINFORMS_TREE_KEY: Readonly<Record<string, number>> = {
  // 1 Beverage (ucDietech_Report_Generator_Beverages: BeverageByHotCold/ByMeal/ByResident/BySet/Total)
  'beverage-by-cold-hot': 1,
  'beverage-by-meal': 1,
  'beverage-by-resident': 1,
  'beverage-by-set': 1,
  'beverage-total': 1,
  // 2 Management Team: 3-7
  'management-team-chargeable': 3,
  'management-team-fiber': 4,
  'management-team-protein': 5,
  'management-team-special-request': 6,
  'management-team-thickener': 7,
  // 8 Meal: 9, 11, 12, 61, 68
  'meal-summary': 9,
  'meal-dislike': 11,
  'meal-feeding-status-assigned': 12,
  'meal-portion': 61,
  'meal-feeding-status-unassigned': 68,
  // 63 Acceptance: 10, 62, 30, 28
  'acceptance-meal': 10,
  'acceptance-monthly': 62,
  'acceptance-nourishments': 30,
  'acceptance-med-pass': 28,
  // 13 Selective Menu: 14, 15
  'selective-menu': 13,
  'selective-menu-non-returned': 14,
  'selective-menu-resident-list': 15,
  // 16 Posting Menus: 54-60, 90
  'posting-menu-daily': 54,
  'posting-menu-diet-specific': 55,
  'posting-menu-extension-sheet': 56,
  'posting-menu-general': 57,
  'posting-menu-general-2': 58,
  'posting-menu-selective-menu': 59,
  'posting-menu-specific-resident': 60,
  'posting-menu-daily-full-day': 90,
  // 17 Production: 97, 98, 99
  'production-tally': 97,
  'production-grocery-list': 98,
  'production-prep-pull': 99,
  // 103 Employees: 101, 104, 107
  'employee-production-tally': 101,
  'employee-tray-tickets': 104,
  'employee-charge-summary': 107,
  // 18 Tray Tickets: 19
  'tray-tickets': 18,
  'tray-ticket-notes': 19,
  // 23 Diet Order: 24, 25, 53, 67
  'diet-order': 23,
  'diet-order-tray-line': 24,
  'consistency-census': 25,
  'diet-order-changes': 53,
  'diet-type-census': 67,
  // 26 Med Pass: 27
  'med-pass-summary': 27,
  // 29 Nourishment: 69 (By Unit / By Floor variant of the same card)
  nourishments: 29,
  supplement: 31,
  birthday: 32,
  // 33 Cost Analysis: 34, 35
  'cost-analysis-menu': 34,
  'cost-analysis-nourishment': 35,
  // 36 Miscellaneous: 37-41, 44, 47, 102, 92 (EHR: 93, 108)
  census: 37,
  'food-allergies': 38,
  prescriber: 39,
  'special-devices': 40,
  'special-needs': 41,
  'drug-allergies': 44,
  skin: 47,
  'menu-cycle-usage': 102,
  'ehr-diet-order-changes': 93,
  'meal-pattern-notes': 108,
  // 42 Nutritional: 43, 45, 46
  'assessment-template': 43,
  'nutritional-recommendations': 45,
  'resident-assessment-list': 46,
  // 48 Weight: 49-52
  'weight-matrix': 49,
  'weight-trigger': 50,
  'weight-significant-change': 51,
  'weight-entry-worksheet': 52,
  // 80 Tube Feed: 64, 65, 66, 106
  'tube-feed-labels': 64,
  'tube-feed-products': 65,
  'tube-feed-list': 66,
  'tube-feed-enteral-feeding-label': 106,
  // 70 Recipes: 71, 96, 100
  'recipe-menu-cycle': 71,
  'recipe-book': 96,
  'recipe-ingredients-menu-cycle': 100,
  // 72 Credits: 73, 74, 75, 88, 89
  'credits-end-of-month-balance': 73,
  'credits-item-usage': 74,
  'credits-end-of-day-balance': 75,
  'credits-summary-by-resident': 88,
  'credits-current-month-summary': 89,
  // 77 Tray Line: 76, 20, 21, 81
  'tray-line-assigned': 76,
  'tray-line-locator': 20,
  'tray-line-overrides': 21,
  'tray-line-unassigned': 81,
  // 78 Resident: 79, 82
  'resident-labels': 79,
  'resident-multiples': 82,
  'dish-up-labels': 91,
  // 94 Nutrition: 95
  'nutritional-analysis': 95,
};

/**
 * Desktop tree nodes that open the same report page as one of the keys above
 * (the Select Case shares the generator, e.g. `Case 48, 49 ' weight matrix`).
 * A desktop favorite on the parent node shows on the website as that card.
 * Node 8 opens Feeding Status - Assigned with production on, Meal Summary
 * without (.vb:1932-1951).
 */
const TREE_KEY_ALIASES: Readonly<Record<number, number>> = {
  2: 3, 16: 57, 17: 97, 26: 27, 42: 43, 48: 49, 63: 10, 69: 29, 70: 71,
  72: 89, 77: 76, 78: 79, 80: 64, 92: 93, 94: 95, 103: 101,
};

/** The key a favorite of this website card is stored under. */
export function favoriteKeyFor(slug: string, rsPKey: number): number {
  // A dropped card's RS key stands for its replacement (retiredReports.ts).
  return SLUG_TO_WINFORMS_TREE_KEY[slug] ?? WEB_ONLY_FAVORITE_KEY_BASE + currentReportPKey(rsPKey);
}

/**
 * Website-only favorite keys of dropped cards -> the key of the card that
 * replaced them: 100135 (Meal Pattern Notes, RS 135) -> 108 (the desktop's
 * Meal Pattern Notes node, RS 140); 100089 (the second Intake Study, RS 89) ->
 * 100094 (Intake Study, RS 94). Production Audit (RS 133) has no replacement.
 */
const RETIRED_FAVORITE_KEYS: Readonly<Record<number, number>> = {
  [WEB_ONLY_FAVORITE_KEY_BASE + 135]: 108,
  [WEB_ONLY_FAVORITE_KEY_BASE + 89]: WEB_ONLY_FAVORITE_KEY_BASE + RETIRED_REPORT_REPLACEMENT[89],
};

/** The key a stored favorite of a dropped card should be rewritten to, or undefined. */
export function retiredFavoriteKeyTarget(fKey: number): number | undefined {
  return RETIRED_FAVORITE_KEYS[fKey];
}

/** True when the key is a desktop tree node (the desktop names it itself). */
export function isDesktopTreeKey(key: number): boolean {
  return key > 0 && key < WEB_ONLY_FAVORITE_KEY_BASE;
}

/** A stored key mapped onto the key the website card uses. */
export function normaliseFavoriteKey(fKey: number, useProduction?: boolean | null): number {
  if (fKey === 8) return useProduction === false ? 9 : 12;
  return TREE_KEY_ALIASES[fKey] ?? RETIRED_FAVORITE_KEYS[fKey] ?? fKey;
}

export interface FavoriteRow {
  pKey_PM_User_ReportFavorite: number;
  fKey_Report: number;
  name?: string | null;
}

const flat = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * "{Facility} Census" -> /^.*census$/ over the flattened name. A name that is
 * only a token ("{Prescriber}") would match anything, so it gives no pattern;
 * callers pass the cards' resolved names (`apiNames`) for those.
 */
function namePattern(reportName: string): RegExp | null {
  const parts = reportName.split(/\{[^}]*\}/).map((p) => escape(flat(p)));
  if (!parts.join('')) return null;
  return new RegExp(`^${parts.join('.*')}$`);
}
const nameMatches = (reportName: string, flatName: string) => namePattern(reportName)?.test(flatName) ?? false;

/** RS key -> [slug, report names the website may have saved for it]. */
let rsIndex: Map<number, Array<{ slug: string; names: string[] }>> | null = null;
function byRsKey(): Map<number, Array<{ slug: string; names: string[] }>> {
  if (rsIndex) return rsIndex;
  const m = new Map<number, Array<{ slug: string; names: string[] }>>();
  const add = (pk: number, slug: string, name: string | undefined) => {
    if (!pk || !name) return;
    const list = m.get(pk) ?? [];
    const hit = list.find((x) => x.slug === slug);
    if (hit) hit.names.push(name);
    else list.push({ slug, names: [name] });
    m.set(pk, list);
  };
  for (const c of REPORT_CONFIGS) add(c.fKey_RS_Report, c.slug, c.reportName);
  for (const s of STATIC_REPORTS) add(s.pKey_RS_Report, s.slug, s.reportName);
  // Favorites the website saved under a dropped card's RS key and name, before
  // the shared-key change. Without these, RS 89 "Intake Study" would read as
  // desktop node 89 (Credits Current Month Summary).
  add(89, 'intake-study', 'Intake Study');
  add(135, 'meal-pattern-notes', 'Meal Pattern Notes');
  rsIndex = m;
  return m;
}

/**
 * The website card a favorite saved by the website before the shared-key
 * change points at, or undefined when it is not one. Those rows carry an RS
 * key and the card's name; desktop rows have no name unless renamed, and a
 * rename is free text that will not spell a report name at that RS key.
 */
export function legacyWebFavoriteSlug(
  fav: FavoriteRow,
  /**
   * More names the website may have saved for an RS key: the API list's
   * names (the classic page saved those) and the cards' resolved names.
   */
  apiNames?: ReadonlyArray<LegacyName>,
): string | undefined {
  const name = (fav.name ?? '').trim();
  if (!name || !isDesktopTreeKey(fav.fKey_Report)) return undefined;
  const flatName = flat(name);
  for (const entry of byRsKey().get(fav.fKey_Report) ?? []) {
    if (entry.names.some((n) => nameMatches(n, flatName))) return entry.slug;
  }
  for (const a of apiNames ?? []) {
    if (a.rsPKey === fav.fKey_Report && nameMatches(a.name, flatName)) return a.slug;
  }
  return undefined;
}

export interface LegacyName { rsPKey: number; slug: string; name: string }

/** The stored favorites that represent this website card. */
export function favoritesForCard<T extends FavoriteRow>(
  favorites: readonly T[] | null | undefined,
  slug: string,
  rsPKey: number,
  useProduction?: boolean | null,
  apiNames?: ReadonlyArray<LegacyName>,
): T[] {
  if (!favorites?.length) return [];
  const key = favoriteKeyFor(slug, rsPKey);
  return favorites.filter((f) => {
    const legacy = legacyWebFavoriteSlug(f, apiNames);
    if (legacy) return legacy === slug;
    return normaliseFavoriteKey(f.fKey_Report, useProduction) === key;
  });
}

/** The row to POST when the user stars this card. */
export function newFavoriteFor(slug: string, rsPKey: number, userId: number, cardName: string) {
  const key = favoriteKeyFor(slug, rsPKey);
  // A desktop node gets no name, as WinForms writes it, so the desktop shows
  // its own text (GetFavoritesText); a website-only card keeps its name.
  return { fKey_Report: key, fKey_User: userId, name: isDesktopTreeKey(key) ? '' : cardName };
}
